import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../store/authStore';
import { graphqlApi } from '../../services/graphql';
import { UserType } from '../../constants/userTypes';
import { CURRENT_TOS_VERSION } from '../../config/tos';
import GlobalNavBar from '../layout/GlobalNavBar';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface LocationState {
  userId: string;
  email: string;
  name: string;
  profileImage?: string;
  oauthProvider?: string;
  tokens?: { accessToken: string; idToken: string };
}

interface CompleteRegistrationFormData {
  name: string;
  email: string;
  userType: UserType;
  tosAccepted: boolean;
  marketingConsent: boolean;
}

/**
 * Registration completion page for new OAuth users.
 * Same layout as RegisterForm but without password fields,
 * pre-filled with data from the OAuth provider.
 */
export const CompleteOAuthRegistration: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const setAuthenticatedUser = useAuthStore((state) => state.setAuthenticatedUser);

  const state = location.state as LocationState | undefined;

  // Pre-fill form with OAuth provider data
  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
  } = useForm<CompleteRegistrationFormData>({
    defaultValues: {
      name: state?.name || '',
      email: state?.email || '',
      userType: '' as UserType,
      tosAccepted: false,
      marketingConsent: false,
    },
  });

  // If no OAuth state was passed, redirect to login
  if (!state?.userId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-gray-600 mb-4">
            {t('auth:sessionExpired', 'Session expired. Please try again.')}
          </p>
          <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
            {t('auth:backToLogin', 'Back to Login')}
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: CompleteRegistrationFormData) => {
    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      const result = await graphqlApi.completeOAuthRegistration({
        userId: state.userId,
        email: data.email,
        name: data.name,
        userType: data.userType,
        profileImage: state.profileImage || null,
        oauthProvider: state.oauthProvider,
        tosAcceptedVersion: CURRENT_TOS_VERSION,
        tosAcceptedAt: now,
        marketingConsent: data.marketingConsent,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to complete registration');
      }

      setAuthenticatedUser({
        userId: state.userId,
        email: data.email,
        name: data.name,
        userType: data.userType,
        createdAt: now,
        updatedAt: now,
      });

      toast.success(t('auth:registrationComplete', 'Registration complete!'));
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('❌ Complete registration error:', error);
      toast.error(error.message || t('auth:failedToCompleteRegistration', 'Failed to complete registration'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />

      {/* Content */}
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:completeRegistration', 'Complete Your Registration')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth:completeRegistrationDesc', 'Welcome! Please fill in the remaining details to continue.')}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Name - pre-filled from OAuth */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('auth:fullName')}
              </label>
              <input
                {...registerField('name', {
                  required: t('auth:errors.nameRequired'),
                  minLength: {
                    value: 2,
                    message: t('auth:errors.nameMinLength'),
                  },
                })}
                type="text"
                className="mt-1 input"
                placeholder={t('auth:enterFullName')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Email - pre-filled from OAuth, read-only */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth:emailAddress')}
              </label>
              <input
                {...registerField('email', {
                  required: t('auth:errors.emailRequired'),
                })}
                type="email"
                autoComplete="email"
                className="mt-1 input bg-gray-50"
                readOnly
              />
            </div>

            {/* Account Type */}
            <div>
              <label htmlFor="userType" className="block text-sm font-medium text-gray-700">
                {t('auth:accountType')}
              </label>
              <select
                {...registerField('userType', {
                  required: t('auth:errors.accountTypeRequired'),
                })}
                className="mt-1 input"
              >
                <option value="">{t('auth:selectAccountType')}</option>
                <option value="host">{t('auth:brandHost')}</option>
                <option value="agency">{t('auth:agency')}</option>
                <option value="influencer">{t('auth:influencerCreator')}</option>
              </select>
              {errors.userType && (
                <p className="mt-1 text-sm text-red-600">{errors.userType.message}</p>
              )}
            </div>

            {/* Terms of Service Acceptance */}
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  {...registerField('tosAccepted', {
                    required: t('auth:errors.tosRequired'),
                  })}
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="tosAccepted" className="ml-2 block text-sm text-gray-700">
                  {t('auth:tosAcceptance')}{' '}
                  <Link
                    to="/legal/terms-of-service"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 underline"
                  >
                    {t('auth:termsOfService')}
                  </Link>
                  {' '}{t('auth:and')}{' '}
                  <Link
                    to="/legal/privacy-policy"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 underline"
                  >
                    {t('auth:privacyPolicy')}
                  </Link>
                </label>
              </div>
              {errors.tosAccepted && (
                <p className="ml-6 text-sm text-red-600">{errors.tosAccepted.message}</p>
              )}

              {/* Marketing Consent (Optional) */}
              <div className="flex items-start">
                <input
                  {...registerField('marketingConsent')}
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="marketingConsent" className="ml-2 block text-sm text-gray-700">
                  {t('auth:marketingConsent')}
                </label>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('auth:completing', 'Completing...')
                : t('auth:completeRegistrationBtn', 'Complete Registration')}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};
